import { ActivityTypeKeys, ActivityTypes } from '@application/features/peopleActivities/constants/activity-types';
import { TASK_DATE_DISPLAY, TASK_TIME_DISPLAY, DATE_DD_MM_YYYY } from '@application/utilities/date-formats';
import dayjs from 'dayjs';
/**
 * Formats date for display in TaskCard
 * @param dateString - Date string from backend
 * @returns Formatted date and time for display in format "Thu, Jul 10 3:25PM"
 */
export const formatTaskDate = (dateString) => {
    const date = dayjs(dateString);
    // Format date and time separately for proper styling
    const dateFormatted = date.format(TASK_DATE_DISPLAY); // "Thu, Jul 10"
    const timeFormatted = date.format(TASK_TIME_DISPLAY); // "3:25PM"
    return {
        date: dateFormatted,
        time: timeFormatted
    };
};
/**
 * Converts local Date object to backend format
 * Sends the date as the user intended without timezone conversion
 * @param date - Date object from frontend form
 * @returns Date string for backend
 */
export const convertLocalToBackend = (date) => {
    if (typeof date === 'string') {
        return date;
    }
    // Format: YYYY-MM-DDTHH:mm:ss (without timezone to preserve user's intent)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};
/**
 * Formats date range for display in EventCard
 * @param startDate - Start date string from backend
 * @param endDate - End date string from backend
 * @returns Formatted date range for display in format "28/9 → 30/9 (2025)"
 */
export const formatEventDateRange = (startDate, endDate) => {
    try {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        if (!start.isValid() || !end.isValid()) {
            return 'Invalid date range';
        }
        const startDay = start.format('D');
        const startMonth = start.format('M');
        const startYear = start.format('YYYY');
        const endDay = end.format('D');
        const endMonth = end.format('M');
        const endYear = end.format('YYYY');
        if (startYear === endYear) {
            // Same year
            if (startMonth === endMonth) {
                // Same month
                return `${startDay}/${startMonth} → ${endDay}/${endMonth} (${endYear})`;
            }
            else {
                // Different months
                return `${startDay}/${startMonth} → ${endDay}/${endMonth} (${endYear})`;
            }
        }
        else {
            // Different years
            return `${startDay}/${startMonth}/${startYear} → ${endDay}/${endMonth}/${endYear}`;
        }
    }
    catch (error) {
        return 'Invalid date range';
    }
};
/**
 * Formats event date for display respecting current locale
 * Example EN: "Friday, 15 Aug 2025", Example DE: "Freitag, 15 Aug 2025"
 * @param dateString - Date string to format
 * @param locale - Optional locale code ('en', 'de', 'nl'). If not provided, uses current dayjs locale.
 */
export const formatEventDate = (dateString, locale) => {
    if (!dateString)
        return '';
    const date = dayjs(dateString);
    if (!date.isValid())
        return '';
    if (locale) {
        return date.locale(locale).format('dddd, DD MMM YYYY');
    }
    return date.format('dddd, DD MMM YYYY');
};
// Formats date range for birthdays in activities. Accepts display type (legacy) or activityTypeCode.
export const GetFormattedDate = (dateString, activityTypeOrCode) => {
    const date = new Date(dateString);
    const isBirthday = activityTypeOrCode === ActivityTypes.Birthday || activityTypeOrCode === ActivityTypeKeys.Birthday;
    if (isBirthday) {
        date.setUTCFullYear(date.getUTCFullYear() - 1);
    }
    return date.toLocaleDateString();
};
/**
 * Formats a registered date string to the format: "00:25 Aug 15, 2025"
 * @param dateString - The date string to format
 * @returns Formatted date string or '—' if invalid
 */
export const formatRegisteredDate = (dateString) => {
    if (!dateString)
        return '—';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime()))
            return '—';
        const hours = date.getHours(); // 0–23
        const minutes = date.getMinutes();
        const hoursStr = hours.toString().padStart(2, '0');
        const minutesStr = minutes.toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${hoursStr}:${minutesStr} ${month} ${day}, ${year}`;
    }
    catch {
        return '—';
    }
};
/**
 * Formats a registered date string to the format: "Aug 15, 2025" (date only, no time)
 * @param dateString - The date string to format
 * @returns Formatted date string or '—' if invalid
 */
export const formatRegisteredDateOnly = (dateString) => {
    if (!dateString)
        return '—';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime()))
            return '—';
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month} ${day}, ${year}`;
    }
    catch {
        return '—';
    }
};
export const getAgeFromDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth)
        return null;
    const birth = dayjs(dateOfBirth);
    if (!birth.isValid())
        return null;
    return dayjs().diff(birth, 'year');
};
export const formatDateOfBirthDisplay = (dateOfBirth) => {
    if (!dateOfBirth)
        return '';
    const date = dayjs(dateOfBirth);
    if (!date.isValid())
        return '';
    return date.format(DATE_DD_MM_YYYY);
};
